/* ==========================================================================
   Metcos - collection card video

   A small "Watch video" button sits on product cards that have one. Tapping
   it opens a floating mini reel BOTTOM LEFT - deliberately the opposite
   corner from the fixed WhatsApp button, which has z-index 55 and was sitting
   on top of the reel. The reel is the transient thing, so the reel moves.

   Tapping the reel expands to a full screen player with sound and a link
   through to the product.

   While a reel is playing, the card it came from is marked - a gold ring on
   the card edge, and its own button turns solid gold and reads "Playing".
   Nothing is drawn over the product photo: an earlier build put a badge on
   .card__inner::after, it collided with Dawn's own pseudo-elements on that
   node and rendered as a huge gold disc across the image.

   Deliberately NO autoplay anywhere. Nothing is downloaded until a shopper
   taps. The click listener is delegated and runs in the capture phase, so
   cards appended by infinite scroll work with no extra wiring.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = {};
  try {
    var cfgEl = document.getElementById('MdjCardVideoConfig');
    if (cfgEl) CFG = JSON.parse(cfgEl.textContent) || {};
  } catch (e) {
    CFG = {};
  }

  if (CFG.enabled === false) return;

  var PLAYING_CLASS = 'mdj-cv-playing';
  var PLAYING_LABEL = CFG.playingLabel || 'Playing';

  var ui = null;
  var current = null;
  var currentCard = null;
  var isFull = false;

  function noop() {}

  // On Save-Data or 2G the full screen player keeps the small rendition too.
  function frugal() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || '';
    return t === '2g' || t === 'slow-2g';
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  var CLOSE_SVG =
    '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
    '<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.7" ' +
    'fill="none" stroke-linecap="round"/></svg>';

  // ------------------------------------------------------------------
  // Marking the card the reel came from
  // ------------------------------------------------------------------

  function labelSpan(card) {
    var btn = card && card.querySelector('.mdj-cv-btn');
    return btn ? btn.querySelector('span') : null;
  }

  function setPlayingLabel(card) {
    var span = labelSpan(card);
    if (!span) return;
    if (span.dataset.mdjLabel == null) span.dataset.mdjLabel = span.textContent;
    span.textContent = PLAYING_LABEL;
  }

  function restoreLabel(card) {
    var span = labelSpan(card);
    if (!span) return;
    if (span.dataset.mdjLabel != null) {
      span.textContent = span.dataset.mdjLabel;
      delete span.dataset.mdjLabel;
    }
  }

  function markCard(card) {
    if (currentCard === card) return;
    unmarkCard();
    currentCard = card || null;
    if (currentCard) {
      currentCard.classList.add(PLAYING_CLASS);
      setPlayingLabel(currentCard);
    }
  }

  function unmarkCard() {
    if (currentCard) {
      currentCard.classList.remove(PLAYING_CLASS);
      restoreLabel(currentCard);
    }
    currentCard = null;
  }

  // ------------------------------------------------------------------
  // The player is created once, on the first tap, and reused after that.
  // ------------------------------------------------------------------

  function build() {
    if (ui) return ui;

    var wrap = el('div', 'mdj-cv');

    var mini = el('div', 'mdj-cv__mini');
    mini.setAttribute('role', 'button');
    mini.setAttribute('tabindex', '0');

    var miniVideo = document.createElement('video');
    miniVideo.className = 'mdj-cv__mini-video';
    miniVideo.muted = true;
    miniVideo.loop = true;
    miniVideo.playsInline = true;
    miniVideo.preload = 'none';
    miniVideo.setAttribute('muted', '');
    miniVideo.setAttribute('playsinline', '');

    var spinner = el('span', 'mdj-cv__spinner');
    var miniTitle = el('span', 'mdj-cv__mini-title');

    var miniClose = el('button', 'mdj-cv__mini-close', CLOSE_SVG);
    miniClose.type = 'button';
    miniClose.setAttribute('aria-label', 'Close video');

    mini.appendChild(miniVideo);
    mini.appendChild(spinner);
    mini.appendChild(miniTitle);
    mini.appendChild(miniClose);

    var full = el('div', 'mdj-cv__fs');
    full.setAttribute('aria-hidden', 'true');

    var stage = el('div', 'mdj-cv__fs-stage');

    var fsVideo = document.createElement('video');
    fsVideo.className = 'mdj-cv__fs-video';
    fsVideo.loop = true;
    fsVideo.playsInline = true;
    fsVideo.preload = 'none';
    fsVideo.setAttribute('playsinline', '');

    var fsClose = el('button', 'mdj-cv__fs-close', CLOSE_SVG);
    fsClose.type = 'button';
    fsClose.setAttribute('aria-label', 'Close video');

    var bar = el('div', 'mdj-cv__fs-bar');
    var fsTitle = el('span', 'mdj-cv__fs-title');
    var fsLink = el('a', 'mdj-cv__fs-cta');
    fsLink.textContent = CFG.viewLabel || 'View product';
    bar.appendChild(fsTitle);
    bar.appendChild(fsLink);

    stage.appendChild(fsVideo);
    stage.appendChild(fsClose);
    stage.appendChild(bar);
    full.appendChild(stage);

    wrap.appendChild(mini);
    wrap.appendChild(full);
    document.body.appendChild(wrap);

    ui = {
      wrap: wrap,
      mini: mini,
      miniVideo: miniVideo,
      miniTitle: miniTitle,
      full: full,
      fsVideo: fsVideo,
      fsTitle: fsTitle,
      fsLink: fsLink
    };

    // Buffering feedback. Without it a slow first frame reads as "broken".
    miniVideo.addEventListener('playing', function () {
      mini.classList.remove('is-loading');
    });
    miniVideo.addEventListener('waiting', function () {
      mini.classList.add('is-loading');
    });

    miniClose.addEventListener('click', function (ev) {
      ev.stopPropagation();
      closeAll();
    });

    mini.addEventListener('click', function () {
      openFull();
    });

    mini.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        openFull();
      }
    });

    fsClose.addEventListener('click', function (ev) {
      ev.stopPropagation();
      closeFull();
    });

    full.addEventListener('click', function (ev) {
      if (ev.target === full) closeFull();
    });

    fsVideo.addEventListener('click', function () {
      if (fsVideo.paused) fsVideo.play().catch(noop);
      else fsVideo.pause();
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape') return;
      if (isFull) closeFull();
      else if (wrap.classList.contains('is-open')) closeAll();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        miniVideo.pause();
        fsVideo.pause();
      } else if (!isFull && wrap.classList.contains('is-open')) {
        miniVideo.play().catch(noop);
      }
    });

    return ui;
  }

  // ------------------------------------------------------------------

  function openMini(data, card) {
    if (!data || !data.small) return;
    build();

    if (isFull) closeFull();
    current = data;
    markCard(card);

    ui.miniTitle.textContent = data.title || '';
    ui.mini.setAttribute(
      'aria-label',
      'Now playing: ' + (data.title || 'product video') + '. Activate to open full screen.'
    );
    if (data.poster) ui.miniVideo.poster = data.poster;

    if (ui.miniVideo.getAttribute('src') !== data.small) {
      ui.miniVideo.src = data.small;
      ui.miniVideo.load();
    }

    ui.miniVideo.muted = true;
    try { ui.miniVideo.currentTime = 0; } catch (e) {}

    ui.mini.classList.add('is-loading');
    ui.wrap.classList.add('is-open');
    ui.miniVideo.play().catch(noop);
  }

  function closeAll() {
    if (!ui) return;
    closeFull();
    ui.miniVideo.pause();
    ui.miniVideo.removeAttribute('src');
    ui.miniVideo.load();
    ui.mini.classList.remove('is-loading');
    ui.wrap.classList.remove('is-open');
    unmarkCard();
    current = null;
  }

  function openFull() {
    if (!ui || !current || CFG.fullscreen === false) return;
    isFull = true;
    ui.miniVideo.pause();

    var src = frugal() ? current.small : current.large || current.small;
    if (ui.fsVideo.getAttribute('src') !== src) {
      ui.fsVideo.src = src;
      ui.fsVideo.load();
    }
    if (current.poster) ui.fsVideo.poster = current.poster;

    ui.fsTitle.textContent = current.title || '';
    ui.fsLink.href = current.url || '#';

    ui.full.classList.add('is-open');
    ui.full.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mdj-cv-locked');

    try { ui.fsVideo.currentTime = 0; } catch (e) {}

    ui.fsVideo.muted = false;
    ui.fsVideo.play().catch(function () {
      ui.fsVideo.muted = true;
      ui.fsVideo.play().catch(noop);
    });
  }

  function closeFull() {
    if (!ui || !isFull) return;
    isFull = false;
    ui.fsVideo.pause();
    ui.full.classList.remove('is-open');
    ui.full.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mdj-cv-locked');
    if (ui.wrap.classList.contains('is-open') && !document.hidden) {
      ui.miniVideo.play().catch(noop);
    }
  }

  // ------------------------------------------------------------------

  document.addEventListener(
    'click',
    function (ev) {
      var t = ev.target;
      var btn = t && t.closest ? t.closest('[data-mdj-cv]') : null;
      if (!btn) return;

      ev.preventDefault();
      ev.stopPropagation();

      openMini(
        {
          small: btn.getAttribute('data-mdj-cv-small'),
          large: btn.getAttribute('data-mdj-cv-large'),
          poster: btn.getAttribute('data-mdj-cv-poster'),
          title: btn.getAttribute('data-mdj-cv-title'),
          url: btn.getAttribute('data-mdj-cv-url')
        },
        btn.closest('.card-wrapper')
      );
    },
    true
  );
})();
